import { useState } from 'react';
import type { FormEvent } from 'react';
import { copyText } from '../src/device';
import type { Session } from '../src/types';
import { changeLogin, errorText, inviteLogin, removeLogin, resendLogin } from './api';
import type { ClientDetail, ShopLogin } from './api';
import { ago, CRM_URL, day } from './format';

type Role = 'owner' | 'staff';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const initials = (l: ShopLogin) => (l.name ?? l.email).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

function standing(l: ShopLogin): [string, string] {
  if (!l.enabled) return ['quiet', 'Switched off'];
  if (l.status === 'invited') return ['watch', 'Invited, has not signed in yet'];
  if (l.status === 'reset') return ['watch', 'Must choose a new password'];
  return ['good', l.lastActiveAt ? `Last used ${ago(l.lastActiveAt).toLowerCase()}` : 'Active'];
}

/** The message to send them yourself, by text, so the invite email isn't a surprise. */
const welcome = (name: string) =>
  `Hi${name ? ` ${name.split(' ')[0]}` : ''}, your login for the LeadsIRL CRM is ready. You'll get an email with a temporary password (check spam if you don't see it). `
  + `Go to ${CRM_URL.replace('https://', '')}, sign in with your email address and that password, then choose your own password.`;

/** Who can sign in to a shop's CRM: invite the owner and staff, resend, switch off, change role, remove. */
export function Logins({ session, d, change }: {
  session: Session;
  d: ClientDetail;
  change: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const c = d.config;
  const id = c.clientId;
  const hasOwner = d.logins.some((l) => l.role === 'owner');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(hasOwner ? 'staff' : 'owner');
  const [busy, setBusy] = useState('');
  // Where the last problem was: a login in the list, or the invite form.
  const [err, setErr] = useState<{ at: 'list' | 'invite'; text: string } | null>(null);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState<{ name: string; email: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const act = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    setErr(null);
    setNote('');
    try {
      let done = '';
      await change(async () => { done = await fn(); });
      setNote(done);
    } catch (e) {
      setErr({ at: key === 'invite' ? 'invite' : 'list', text: errorText(e) });
    } finally {
      setBusy('');
    }
  };

  const onInvite = (e: FormEvent) => {
    e.preventDefault();
    const who = email.trim().toLowerCase();
    if (!EMAIL.test(who)) { setErr({ at: 'invite', text: 'Type their email address.' }); return; }
    const nm = name.trim();
    void act('invite', async () => {
      await inviteLogin(session, id, { email: who, role, ...(nm ? { name: nm } : {}) });
      setSent({ name: nm, email: who });
      setCopied(false);
      setName('');
      setEmail('');
      setRole('staff');
      return '';
    });
  };

  const resend = (l: ShopLogin) => {
    if (l.status !== 'invited' && !window.confirm(`Send ${l.email} a password reset?\n\nThey'll get a code by email and must choose a new password the next time they sign in.`)) return;
    void act(l.email, async () => ((await resendLogin(session, id, l.email)) === 'invite'
      ? `Invite sent again to ${l.email}.`
      : `${l.email} must choose a new password at their next sign-in. A code is on its way by email.`));
  };
  const setRoleOf = (l: ShopLogin, next: Role) => {
    if (next === 'staff' && l.role === 'owner' && d.logins.filter((x) => x.role === 'owner').length === 1
      && !window.confirm(`${l.email} is the only owner. Make them staff anyway?\n\nNobody will be able to change ${c.businessName}'s settings from their CRM.`)) return;
    void act(l.email, async () => {
      await changeLogin(session, id, l.email, { role: next });
      return `${l.email} is now ${next === 'owner' ? 'an owner' : 'staff'}. It takes effect within the hour.`;
    });
  };
  const setOn = (l: ShopLogin, on: boolean) => {
    if (!on && !window.confirm(`Switch off ${l.email}?\n\nThey're signed out everywhere and can't sign in until you switch them on again.`)) return;
    void act(l.email, async () => {
      await changeLogin(session, id, l.email, { enabled: on });
      return on ? `${l.email} can sign in again.` : `${l.email} is switched off and signed out everywhere.`;
    });
  };
  const remove = (l: ShopLogin) => {
    if (!window.confirm(`Remove ${l.email}?\n\nTheir login is deleted and they're signed out everywhere. You can invite them again later.`)) return;
    void act(l.email, async () => {
      await removeLogin(session, id, l.email);
      return `${l.email} was removed.`;
    });
  };

  const copy = async () => {
    if (!sent) return;
    setCopied(await copyText(welcome(sent.name)));
  };

  return (
    <div className="logins-page">
      <section className="card">
        <h2>Who can sign in</h2>
        <p className="fine">These people sign in to {c.businessName}'s CRM at {CRM_URL.replace('https://', '')}.</p>
        {err?.at === 'list' ? <p className="err" role="alert">{err.text}</p> : note && <p className="saved" role="status">{note}</p>}
        {d.logins.length === 0 ? (
          <p className="empty">Nobody can sign in yet. Invite the owner below.</p>
        ) : (
          <ul className="logins">
            {d.logins.map((l) => {
              const [tone, text] = standing(l);
              const working = busy === l.email;
              return (
                <li key={l.email} className={l.enabled ? '' : 'off'}>
                  <span className="avatar" aria-hidden="true">{initials(l)}</span>
                  <div className="person">
                    <b>{l.name || l.email}</b>
                    {l.name && <span className="sub">{l.email}</span>}
                    <span className="meta">
                      <span className={`role ${l.role}`}>{l.role === 'owner' ? 'Owner' : 'Staff'}</span>
                      <span className={`state ${tone}`}>{text}</span>
                      {l.createdAt && <span className="sub">Added {day(l.createdAt)}</span>}
                    </span>
                  </div>
                  <div className="row-actions">
                    {working ? <span className="sub">Working…</span> : (
                      <>
                        {l.enabled && <button type="button" className="link" disabled={busy !== ''} onClick={() => resend(l)}>{l.status === 'invited' ? 'Resend invite' : 'Reset password'}</button>}
                        <button type="button" className="link" disabled={busy !== ''} onClick={() => setRoleOf(l, l.role === 'owner' ? 'staff' : 'owner')}>{l.role === 'owner' ? 'Make staff' : 'Make owner'}</button>
                        <button type="button" className="link" disabled={busy !== ''} onClick={() => setOn(l, !l.enabled)}>{l.enabled ? 'Switch off' : 'Switch on'}</button>
                        <button type="button" className="link danger-link" disabled={busy !== ''} onClick={() => remove(l)}>Remove</button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {sent && (
        <section className="card sent" aria-live="polite">
          <h2>Invite sent to {sent.email}</h2>
          <p className="fine">Cognito emails them a temporary password. Text them this too, so the email isn't a surprise:</p>
          <blockquote>{welcome(sent.name)}</blockquote>
          <div className="actions">
            <button type="button" className="primary" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy message'}</button>
            <button type="button" className="ghost" onClick={() => setSent(null)}>Done</button>
          </div>
        </section>
      )}

      <form className="card" onSubmit={onInvite} noValidate>
        <h2>Invite someone</h2>
        <div className="fields">
          <label>Name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Mike Kowalski" autoComplete="off" /></label>
          <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mike@kowalskiroofing.com" autoComplete="off" /></label>
        </div>
        <div className="field">
          <span className="label" id="role">Role</span>
          <div className="choices" role="radiogroup" aria-labelledby="role">
            <button type="button" role="radio" aria-checked={role === 'owner'} className={role === 'owner' ? 'on' : ''} onClick={() => setRole('owner')}>
              <b>Owner</b>
              <span>Everything in their CRM, settings too.</span>
            </button>
            <button type="button" role="radio" aria-checked={role === 'staff'} className={role === 'staff' ? 'on' : ''} onClick={() => setRole('staff')}>
              <b>Staff</b>
              <span>Leads, texting, photos and the calendar. No settings. For an office manager, a spouse, a helper.</span>
            </button>
          </div>
        </div>
        {err?.at === 'invite' && <p className="err" role="alert">{err.text}</p>}
        <div className="actions">
          <button className="primary" type="submit" disabled={busy !== ''}>{busy === 'invite' ? 'Sending…' : 'Send invite'}</button>
        </div>
        <p className="fine">A login belongs to this shop for good. To move someone to another shop, remove them here and invite them there. An email can have one login only.</p>
      </form>
    </div>
  );
}
